import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type GeoLocation } from "@src/appV2/Location";
import { getGeofenceStatus } from "@src/appV2/Location/utils/getGeofenceStatus";
import { addHours, isAfter, parseISO } from "date-fns";
import { useCallback, useState } from "react";

/**
 * Distance in miles for GPS fallback geofence check for missed punch requests.
 */
const MPR_GEOFENCE_DISTANCE_IN_MILES = 0.25;

/**
 * Number of hours after shift end when HyperTrack tracking stops.
 * After this time, we skip HyperTrack check and go directly to GPS fallback.
 */
const HOURS_AFTER_SHIFT_END_HT_STOPS_TRACKING = 10;

type GeofenceCheckResult =
  | { status: "inside" }
  | { status: "outside" }
  | { status: "unknown"; error?: unknown };

interface UseMissedPunchRequestGeofenceCheckParams {
  shiftId: string;
  shiftEnd: string;
  workerId: string;
  workplaceLocation: GeoLocation | undefined;
}

interface UseMissedPunchRequestGeofenceCheckResult {
  isCheckingGeofence: boolean;
  checkGeofence: (params: UseMissedPunchRequestGeofenceCheckParams) => Promise<GeofenceCheckResult>;
}

/**
 * Hook to check if worker is inside the workplace geofence for missed punch requests.
 *
 * This hook:
 * - Uses HyperTrack for geofence check if tracking is still active (within 10h of shift end)
 * - Falls back to GPS with 0.25 mile distance
 * - Returns "inside" if worker is inside geofence
 * - Returns "outside" if worker is outside geofence
 * - Returns "unknown" if unable to determine
 */
export function useMissedPunchRequestGeofenceCheck(): UseMissedPunchRequestGeofenceCheckResult {
  const [isCheckingGeofence, setIsCheckingGeofence] = useState(false);

  const hyperTrackGeotagEvents = useCbhFlag(CbhFeatureFlag.HYPER_TRACK_GEOTAG_EVENTS, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.HYPER_TRACK_GEOTAG_EVENTS],
  });

  const isHyperTrackEnabled = useCbhFlag(CbhFeatureFlag.HYPERTRACK_CLOCK_IN_OUT, {
    defaultValue: false,
  });

  const checkGeofence = useCallback(
    async (params: UseMissedPunchRequestGeofenceCheckParams): Promise<GeofenceCheckResult> => {
      const { shiftId, shiftEnd, workerId, workplaceLocation } = params;

      setIsCheckingGeofence(true);

      try {
        // Check if we should skip HyperTrack (more than 10h after shift end)
        const shiftEndDate = parseISO(shiftEnd);
        const htTrackingStopsAt = addHours(shiftEndDate, HOURS_AFTER_SHIFT_END_HT_STOPS_TRACKING);
        const shouldSkipHyperTrack = isAfter(new Date(), htTrackingStopsAt);

        const result = await getGeofenceStatus({
          requestUniqueId: {
            workerId,
            shiftId,
          },
          action: "mpr-geofence-check",
          hyperTrackGeotagEvents,
          // Skip HyperTrack if more than 10h after shift end
          isHyperTrackEnabled: isHyperTrackEnabled && !shouldSkipHyperTrack,
          fallbackGeofenceDistanceInMiles: MPR_GEOFENCE_DISTANCE_IN_MILES,
          fallbackGeofenceLocation: workplaceLocation,
        });

        logEvent(APP_V2_USER_EVENTS.MPR_GEOFENCE_CHECK_COMPLETED, {
          shiftId,
          workerId,
          isInsideGeofence: result.isInsideGeofence,
          checkProviders: result.checkProviders,
          hasError: Boolean(result.error),
          shouldSkipHyperTrack,
        });

        // If there's an error and we couldn't determine location, return unknown
        if (result.error && !result.isInsideGeofence) {
          return { status: "unknown", error: result.error };
        }

        return result.isInsideGeofence ? { status: "inside" } : { status: "outside" };
      } catch (error) {
        logEvent(APP_V2_USER_EVENTS.MPR_GEOFENCE_CHECK_FAILED, {
          shiftId,
          workerId,
          error: error instanceof Error ? error.message : String(error),
        });

        return { status: "unknown", error };
      } finally {
        setIsCheckingGeofence(false);
      }
    },
    [hyperTrackGeotagEvents, isHyperTrackEnabled]
  );

  return {
    isCheckingGeofence,
    checkGeofence,
  };
}
